import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const AVATAR_COLORS = [
  '#6366f1', '#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6', '#06b6d4',
];
const avatarColor = (seed = '') => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initials = (name = '') =>
  (name || '?').trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

function SearchResults() {
  const query       = useQuery();
  const searchTerm  = query.get('query') || '';
  const location    = useLocation();
  const leads       = location.state?.leads       || [];
  const leadAssigned = location.state?.leadAssigned || [];
  const navigate    = useNavigate();
  const total       = leads.length + leadAssigned.length;

  return (
    <div style={{ padding: '28px 20px', minHeight: '100vh', background: '#f4f6fb' }}>
      <style>{`
        .sr-card { transition: box-shadow .18s, transform .18s; }
        .sr-card:hover { box-shadow: 0 8px 28px rgba(52,71,103,0.13) !important; transform: translateY(-2px); }
        .sr-btn { transition: opacity .15s, transform .15s; }
        .sr-btn:hover { opacity: .88; transform: translateY(-1px); }
      `}</style>

      {/* ── Header ── */}
      <div style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '6px' }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, flexShrink: 0,
            background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 20,
          }}>
            <i className="fas fa-search" />
          </div>
          <div>
            <h4 style={{ margin: 0, fontWeight: 700, color: '#1a1f36', fontSize: 22 }}>
              Search Results
            </h4>
            <p style={{ margin: 0, fontSize: 14, color: '#8392ab' }}>
              {total > 0
                ? <><strong style={{ color: '#344767' }}>{total}</strong> result{total !== 1 ? 's' : ''} for <strong style={{ color: '#6366f1' }}>"{searchTerm}"</strong></>
                : <>No results found for <strong style={{ color: '#6366f1' }}>"{searchTerm}"</strong></>}
            </p>
          </div>
        </div>
      </div>

      {/* ── No results ── */}
      {total === 0 && (
        <div style={{
          background: '#fff', borderRadius: 20, padding: '52px 24px',
          textAlign: 'center', boxShadow: '0 2px 12px rgba(52,71,103,0.06)',
        }}>
          <i className="fas fa-search" style={{ fontSize: 52, color: '#e9ecef', display: 'block', marginBottom: 16 }} />
          <p style={{ fontWeight: 700, color: '#525f7f', fontSize: 16, margin: '0 0 6px' }}>Nothing found</p>
          <p style={{ fontSize: 13, color: '#8392ab', margin: 0 }}>
            Try a different name, email, phone, or lead code.
          </p>
        </div>
      )}

      {/* ── Leads section ── */}
      {leads.length > 0 && (
        <Section
          icon="fa-users"
          color="#6366f1"
          title="Leads"
          count={leads.length}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>
            {leads.map(lead => {
              const name  = [lead.firstName, lead.lastName].filter(Boolean).join(' ') || 'Unknown';
              const color = avatarColor(lead.email || name);
              return (
                <div
                  key={lead.id}
                  className="sr-card"
                  style={{
                    background: '#fff', borderRadius: 16,
                    boxShadow: '0 2px 10px rgba(52,71,103,0.07)',
                    padding: '18px 20px',
                    display: 'flex', flexDirection: 'column', gap: 12,
                  }}
                >
                  {/* Avatar + name */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                      background: color + '22', color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 15, border: `2px solid ${color}44`,
                    }}>
                      {initials(name)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontWeight: 700, color: '#1a1f36', margin: 0, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {name}
                      </p>
                      {lead.leadCode && (
                        <span style={{
                          background: '#e0e7ff', color: '#4338ca',
                          borderRadius: 6, padding: '1px 8px', fontSize: 11, fontWeight: 600,
                        }}>
                          {lead.leadCode}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Details */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {lead.email && <InfoRow icon="fa-envelope" value={lead.email} color="#6366f1" />}
                    {lead.contactNumber && <InfoRow icon="fa-phone" value={lead.contactNumber} color="#10b981" />}
                    {lead.address && <InfoRow icon="fa-map-marker-alt" value={lead.address} color="#f59e0b" />}
                    {lead.source && <InfoRow icon="fa-globe" value={lead.source} color="#8b5cf6" />}
                  </div>

                  <button
                    className="sr-btn"
                    onClick={() => navigate(`/editlead/${lead.id}`)}
                    style={{
                      marginTop: 'auto', width: '100%',
                      background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                      border: 'none', borderRadius: 10,
                      padding: '9px 0', color: '#fff', fontWeight: 600,
                      fontSize: 13, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                    }}
                  >
                    <i className="fas fa-pencil-alt" /> Edit Lead
                  </button>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* ── Lead Assignments section ── */}
      {leadAssigned.length > 0 && (
        <Section
          icon="fa-clipboard-list"
          color="#10b981"
          title="Lead Assignments"
          count={leadAssigned.length}
          style={{ marginTop: 28 }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>
            {leadAssigned.map(la => {
              const lead  = la.lead || {};
              const name  = lead.firstName || 'Unknown';
              const color = avatarColor(lead.email || name);
              const status = la.leadStatus?.leadStatusName || la.leadStatusId || '—';
              return (
                <div
                  key={la.id}
                  className="sr-card"
                  style={{
                    background: '#fff', borderRadius: 16,
                    boxShadow: '0 2px 10px rgba(52,71,103,0.07)',
                    padding: '18px 20px',
                    display: 'flex', flexDirection: 'column', gap: 12,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                      background: color + '22', color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 15, border: `2px solid ${color}44`,
                    }}>
                      {initials(name)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontWeight: 700, color: '#1a1f36', margin: 0, fontSize: 15 }}>{name}</p>
                      <span style={{
                        background: '#d1fae5', color: '#065f46',
                        borderRadius: 6, padding: '1px 8px', fontSize: 11, fontWeight: 600,
                      }}>
                        {status}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {lead.contactNumber && <InfoRow icon="fa-phone" value={lead.contactNumber} color="#10b981" />}
                    {la.conversationComment && <InfoRow icon="fa-comment-dots" value={la.conversationComment} color="#f59e0b" />}
                    {la.followUpDate && (
                      <InfoRow
                        icon="fa-calendar-check"
                        value={`Follow-up: ${new Date(la.followUpDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                        color="#6366f1"
                      />
                    )}
                  </div>

                  <button
                    className="sr-btn"
                    onClick={() => navigate(`/editleadassigned/${la.id}`)}
                    style={{
                      marginTop: 'auto', width: '100%',
                      background: 'linear-gradient(135deg,#10b981,#059669)',
                      border: 'none', borderRadius: 10,
                      padding: '9px 0', color: '#fff', fontWeight: 600,
                      fontSize: 13, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                    }}
                  >
                    <i className="fas fa-pencil-alt" /> Edit Assignment
                  </button>
                </div>
              );
            })}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ icon, color, title, count, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 9,
          background: color + '18',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color, fontSize: 14,
        }}>
          <i className={`fas ${icon}`} />
        </div>
        <h5 style={{ margin: 0, fontWeight: 700, color: '#344767', fontSize: 16 }}>{title}</h5>
        <span style={{
          background: color + '18', color,
          borderRadius: 20, padding: '2px 10px',
          fontSize: 12, fontWeight: 700,
        }}>
          {count}
        </span>
      </div>
      {children}
    </div>
  );
}

function InfoRow({ icon, value, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{
        width: 22, height: 22, borderRadius: 6, flexShrink: 0,
        background: color + '18',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color, fontSize: 10,
      }}>
        <i className={`fas ${icon}`} />
      </div>
      <span style={{
        fontSize: 13, color: '#525f7f',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {value}
      </span>
    </div>
  );
}

export default SearchResults;
